import {
  REFERENCE_PAGE_SIZE,
  type AccountDeletionStatus,
  type ChannelRow,
  type ChannelSortKey,
  type CompetitionPlanRow,
  type CompetitionPlanSortKey,
  type CompetitorRow,
  type CompetitorSortKey,
  type DeletionRequestRow,
  type DeletionRequestSortKey,
  type OutletClassificationRow,
  type OutletClassificationSortKey,
  type OutletListRow,
  type OutletSortKey,
  type Page,
  type Permission,
  type PermissionCatalogueEntry,
  type PriceIndexSettings,
  type RegionRow,
  type RegionSortKey,
  type RoleRow,
  type SkuCategoryRow,
  type SkuCategorySortKey,
  type SkuGroupingRow,
  type SkuGroupingSortKey,
  type SkuRow,
  type SkuSortKey,
  type SkuType,
  type SortDir,
  type UserRow,
  type UserSortKey,
} from '@fieldvision/contracts';
import { api, useAccessScope, can, withQuery } from '@fieldvision/web-core';
import { useQuery } from '@tanstack/react-query';

/**
 * Reference lists for pickers.
 *
 * Every one is gated on the permission its endpoint requires. Without the
 * `enabled` guard a supervisor opening the reports screen fires a request for
 * `/auth/users` that comes back 403, and react-query surfaces it as an error
 * banner on a screen that is working perfectly well — the filter simply has one
 * fewer option.
 *
 * These are cached hard. Regions and channels change a few times a year.
 *
 * WHY THESE ASK FOR ONE LARGE PAGE
 * --------------------------------
 * They back `<select>` dropdowns, where paging is not a smaller version of the
 * list — it is a filter that hides options with no way to reveal them. So they
 * request `REFERENCE_PAGE_SIZE` and return `.data` as a plain array, the shape
 * every picker already expects.
 *
 * That is safe for regions, channels, classifications, categories and
 * competitors, which are bounded by business reality rather than by data growth.
 *
 * IT WAS NOT SAFE FOR SKUS, OUTLETS OR USERS, and that limit is no longer
 * accepted. Those three grow without bound, and asking for one 200-row page
 * meant row 201 could not be selected — silently, because a short list looks
 * complete. They are served instead by the `*Options` hooks below, which keep
 * one SMALL page, pass the search term to the server, and return the whole
 * `Page` so the picker can say how much it is not showing. See `Combobox`.
 *
 * So the rule for anything added here: if the list can grow, it gets an
 * `*Options` hook and a `Combobox`, not a `<select>`.
 */

/**
 * The page params every paged hook takes, from `useListState().params`.
 *
 * A `type` rather than an `interface` on purpose: `withQuery` takes
 * `Record<string, QueryValue>`, and TypeScript gives object type ALIASES an
 * implicit index signature while interfaces get none. As an interface this fails
 * to assign, with an error that reads like the values are wrong.
 */
type PageParams<SortKey extends string> = {
  page: number;
  pageSize: number;
  sort: SortKey;
  dir: SortDir;
};

/**
 * Picker-shaped: one large page, unwrapped to a bare array.
 *
 * `sort: 'name'` is right for every picker EXCEPT regions, which has no `name`
 * column since migration 0027 — see `useRegions`. The sort key reaches an ORDER
 * BY, so the server validates it against the endpoint's key list and answers 400
 * for anything else; a wrong default here is an empty picker, not a mis-sorted
 * one.
 */
const referencePage = { page: 1, pageSize: REFERENCE_PAGE_SIZE, sort: 'name', dir: 'asc' } as const;

/**
 * Keeps the previous page on screen while the next one loads.
 *
 * For a table that means a page change does not flash the empty state, which
 * reads as "your filter matched nothing" for as long as the request takes. For
 * a picker it matters more: without it every settled search swaps in a fresh
 * cache entry with no data, and the popover shows "no matches" for ~150ms in the
 * middle of a word — the one message this control must never get wrong.
 */
const keepPreviousPage = <T,>(previous: T | undefined) => previous;

/**
 * One popover's worth of rows.
 *
 * Not `DEFAULT_PAGE_SIZE`, because this is a dropdown rather than a table: the
 * list is capped at 18rem and shows about seven rows, so twenty is three flicks
 * and an honest footer. The staff app's SkuPicker uses fifty, which is right for
 * a full-screen sheet with a "muat lebih banyak" button and wrong here.
 */
const OPTION_PAGE_SIZE = 20;

export function useRegions(options: { includeInactive?: boolean } = {}) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['regions', options.includeInactive ?? false],
    queryFn: () =>
      api.get<Page<RegionRow>>(
        withQuery('/masterdata/regions', {
          ...referencePage,
          /*
           * BY THE SUB-DISTRIBUTOR CODE, because that is what the label leads
           * with — `regionLabel` renders `900024 - BANDUNG`. An area-sorted list
           * of code-first labels has an unordered leading token, so it cannot be
           * scanned by eye, and a native `<select>`'s keyboard type-ahead
           * matches the label PREFIX, so typing `900` would land nowhere useful.
           * The sort and the label format are one decision.
           */
          sort: 'subdistCode' satisfies RegionSortKey,
          includeInactive: options.includeInactive,
        }),
      ),
    select: (page) => page.data,
    enabled: can(scope.data, 'outlet:read'),
    staleTime: 10 * 60_000,
  });
}

/**
 * Outlets for the type-ahead picker.
 *
 * KEEPS THE `Page` ENVELOPE, unlike the reference hooks above. `total` is not
 * decoration — it is the difference between "these are the twenty matches" and
 * "these are twenty of four hundred", and the hook this replaced dropped exactly
 * that field.
 *
 * `regionId` GOES TO THE SERVER. `FilterBar` used to narrow the outlet options
 * by region in the browser, which was fine over a list that claimed to be
 * everything and is wrong over a page: filtering an already-narrowed twenty rows
 * hides outlets that do match. The endpoint has taken `regionId` all along.
 *
 * `enabled` is a caller-supplied gate, ANDed with the permission one. Hooks
 * cannot be conditional, so `FilterBar` calls this even when it is not rendering
 * an outlet picker; without the flag the dashboard would fire a request for
 * options it never displays, on a page already making eleven.
 */
export function useOutletOptions(
  filters: {
    search?: string;
    regionId?: string;
    channelId?: string;
    classificationId?: string;
    enabled?: boolean;
  } = {},
) {
  const scope = useAccessScope();
  const search = filters.search?.trim() ?? '';
  return useQuery({
    queryKey: [
      'outlets',
      'options',
      search,
      filters.regionId ?? '',
      filters.channelId ?? '',
      filters.classificationId ?? '',
    ],
    queryFn: () =>
      api.get<Page<OutletListRow>>(
        withQuery('/masterdata/outlets', {
          page: 1,
          pageSize: OPTION_PAGE_SIZE,
          sort: 'name' satisfies OutletSortKey,
          dir: 'asc',
          // Retired outlets still carry visits, so a report may legitimately be
          // filtered to one. Same reason the old picker passed it.
          includeInactive: true,
          regionId: filters.regionId,
          channelId: filters.channelId,
          classificationId: filters.classificationId,
          // `''` is a 400 — the endpoint's schema is `z.string().min(1)` — and
          // `withQuery` would drop it anyway. Explicit so nobody re-adds it.
          search: search || undefined,
        }),
      ),
    enabled: (filters.enabled ?? true) && can(scope.data, 'outlet:read'),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousPage,
  });
}

/**
 * SKUs for the type-ahead picker.
 *
 * THE HOOK THIS REPLACED IS WHY THE COMBOBOX EXISTS. It selected `.data` off a
 * 200-row page, so a catalogue of 412 rendered as a complete-looking list of
 * 200 and SKU 201 could not be filtered on, paired, or even seen. The endpoint
 * has ILIKEd name OR code the whole time (`masterdata.repository.listSkus`) —
 * no API change was needed for any of this, which is the strongest evidence the
 * ceiling was an accident rather than a decision.
 *
 * NOT FETCHED UNTIL THE PICKER OPENS. Two report screens mount this and neither
 * needs a catalogue to draw a table; `/planogram` used to fire a 200-row SKU
 * request on every page load for a control most visits never touch.
 *
 * The `['skus', …]` prefix is load-bearing: `SkusPage` invalidates the bare
 * `['skus']` key on save, and a picker filed under a new prefix would go on
 * serving a renamed SKU's old name.
 */
export function useSkuOptions(
  filters: { search?: string; type?: SkuType; competitorId?: string; enabled?: boolean } = {},
) {
  const scope = useAccessScope();
  const search = filters.search?.trim() ?? '';
  return useQuery({
    queryKey: ['skus', 'options', search, filters.type ?? '', filters.competitorId ?? ''],
    queryFn: () =>
      api.get<Page<SkuRow>>(
        withQuery('/masterdata/skus', {
          page: 1,
          pageSize: OPTION_PAGE_SIZE,
          sort: 'name' satisfies SkuSortKey,
          dir: 'asc',
          type: filters.type,
          competitorId: filters.competitorId,
          search: search || undefined,
        }),
      ),
    enabled: (filters.enabled ?? true) && can(scope.data, 'sku:read'),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousPage,
  });
}

export function useCompetitors(options: { includeInactive?: boolean } = {}) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['competitors', options.includeInactive ?? false],
    queryFn: () =>
      api.get<Page<CompetitorRow>>(
        withQuery('/masterdata/competitors', { ...referencePage, includeInactive: options.includeInactive }),
      ),
    select: (page) => page.data,
    enabled: can(scope.data, 'sku:read'),
    staleTime: 10 * 60_000,
  });
}

/**
 * The SKU grouping picker — the second classification axis (0054).
 *
 * `includeInactive` for exactly the reason it matters on `useSkuCategories`:
 * the SKU form must be able to render the grouping an existing SKU already
 * points at, even after that grouping has been retired. Without it the select
 * falls back to its placeholder and saving the form would silently regroup the
 * SKU to whatever the user picked instead.
 */
export function useSkuGroupings(options: { includeInactive?: boolean } = {}) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['sku-groupings', options.includeInactive ?? false],
    queryFn: () =>
      api.get<Page<SkuGroupingRow>>(
        withQuery('/masterdata/sku-groupings', {
          ...referencePage,
          includeInactive: options.includeInactive,
        }),
      ),
    select: (page) => page.data,
    enabled: can(scope.data, 'sku:read'),
    staleTime: 10 * 60_000,
  });
}

/**
 * The SKU category picker.
 *
 * `includeInactive` matters here in a way it does not for most pickers: the SKU
 * form has to be able to render the category an existing SKU already points at,
 * and that category may since have been retired. Without it the select silently
 * falls back to its placeholder and saving the form would reassign the SKU to
 * whatever the user picked instead — a data change nobody asked for.
 */
export function useSkuCategories(options: { includeInactive?: boolean } = {}) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['sku-categories', options.includeInactive ?? false],
    queryFn: () =>
      api.get<Page<SkuCategoryRow>>(
        withQuery('/masterdata/sku-categories', {
          ...referencePage,
          includeInactive: options.includeInactive,
        }),
      ),
    select: (page) => page.data,
    enabled: can(scope.data, 'sku:read'),
    staleTime: 10 * 60_000,
  });
}

/**
 * Salespeople and administrators for the type-ahead picker.
 *
 * `permission` is the filter pickers should reach for. "The salespeople" is not
 * a role — it is whoever can start a visit — and since roles became
 * administrable rows (ADR-0006) a hardcoded `roleId` cannot express it: the
 * installation may have renamed SALES, split it in two, or never had one.
 * `roleId` remains for the administration screen, where the user genuinely did
 * pick a role from a list.
 *
 * Paged and server-searched for the same reason SKUs are — a distributor with
 * three hundred field staff had a picker that stopped at two hundred of them,
 * and the two hundred and first could neither be reported on nor given a visit
 * target. The endpoint already searched; only the caller was capping.
 */
export function useUserOptions(
  filters: {
    search?: string;
    roleId?: string;
    permission?: Permission;
    enabled?: boolean;
  } = {},
) {
  const scope = useAccessScope();
  const search = filters.search?.trim() ?? '';
  return useQuery({
    queryKey: ['users', 'options', search, filters.roleId ?? '', filters.permission ?? ''],
    queryFn: () =>
      api.get<Page<UserRow>>(
        withQuery('/auth/users', {
          page: 1,
          pageSize: OPTION_PAGE_SIZE,
          sort: 'name' satisfies UserSortKey,
          dir: 'asc',
          roleId: filters.roleId,
          permission: filters.permission,
          search: search || undefined,
        }),
      ),
    enabled: (filters.enabled ?? true) && can(scope.data, 'user:read'),
    staleTime: 60_000,
    placeholderData: keepPreviousPage,
  });
}

/**
 * The roles this installation has — FR-13, ADR-0006.
 *
 * Not a constant, because roles are rows an administrator can add, rename and
 * delete. Every picker and every matrix column comes from here; two hardcoded
 * `ROLES` arrays used to live in the pages that needed them, and adding a role
 * meant editing both plus the contract plus a migration.
 *
 * Short staleTime relative to the other reference lists: the screen that edits
 * roles is the same screen that renders them, so a stale list here is visible
 * within seconds of the edit that made it stale.
 */
export function useRoles() {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleRow[]>('/auth/roles'),
    enabled: can(scope.data, 'user:read'),
    staleTime: 60_000,
  });
}

/**
 * Every permission the API enforces, with its label and group.
 *
 * The matrix used to carry this list itself, and the copy had drifted —
 * `target:read` and `target:write` were missing, so two permissions the API
 * accepted could not be granted through the only screen that grants them.
 * Reading it from the server means a permission added in a migration appears
 * without a front-end release.
 */
export function usePermissionCatalogue() {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['permission-catalogue'],
    queryFn: () => api.get<PermissionCatalogueEntry[]>('/auth/permissions/catalogue'),
    enabled: can(scope.data, 'user:read'),
    staleTime: 60 * 60_000,
  });
}

/**
 * The FR-14 grading band — for LEGENDS, not for verdicts.
 *
 * NO `enabled` PERMISSION GATE, unlike every other hook in this file, and the
 * endpoint has no `@RequirePermissions` to match. The grades this number
 * produces already ride on rows any `report:read` caller may read, and the price
 * index page prints the band beside those badges so a reader can tell what the
 * colours mean. Gating it would leave a supervisor looking at coloured badges
 * with no key.
 *
 * CACHED FOR AN HOUR, which would be reckless if anything computed with it. Only
 * copy is rendered from it — the badges themselves carry a `grade` the server
 * decided — so a stale copy misprints a legend for at most an hour rather than
 * grading a plan wrongly. The settings form invalidates `['settings']` and the
 * whole `['price-index']` key on save, because every cached grade on every
 * cached page was computed with the previous number.
 */
export function usePriceIndexSettings() {
  return useQuery({
    queryKey: ['settings', 'price-index'],
    queryFn: () => api.get<PriceIndexSettings>('/masterdata/settings/price-index'),
    staleTime: 60 * 60_000,
  });
}

/**
 * The channel master, as picker options.
 *
 * RETURNS ROWS, NOT STRINGS. This used to be a bare `string[]` from a
 * `SELECT DISTINCT` over `outlets.channel`, because there was no channel table;
 * migration 0044 made channels real, so the picker binds an id and shows a name.
 * Every caller that put `filters.channel` straight into a `<option value>` needs
 * the id instead — the value a report filters by is no longer the label.
 *
 * Same shape as `useSkuCategories`: one large page of a real master, not a
 * projection. `.data` so callers get a plain array.
 */
export function useChannels(options: { includeInactive?: boolean } = {}) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['channels', options.includeInactive ?? false],
    queryFn: () =>
      api.get<Page<ChannelRow>>(
        withQuery('/masterdata/channels', {
          ...referencePage,
          includeInactive: options.includeInactive,
        }),
      ),
    select: (page) => page.data,
    enabled: can(scope.data, 'outlet:read'),
    staleTime: 10 * 60_000,
  });
}

/**
 * The classifications of ONE channel — the cascading half of the outlet form and
 * the report filter bar.
 *
 * `enabled` on a channel being chosen, deliberately. A classification list with
 * no channel is every classification in the system, which is a picker where
 * "Reguler" appears four times with nothing to tell them apart. The caller
 * disables the control until a channel is selected and this hook simply does not
 * fire until then.
 */
export function useClassifications(
  options: { channelId?: string; includeInactive?: boolean } = {},
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['outlet-classifications', options.channelId ?? '', options.includeInactive ?? false],
    queryFn: () =>
      api.get<Page<OutletClassificationRow>>(
        withQuery('/masterdata/outlet-classifications', {
          ...referencePage,
          channelId: options.channelId,
          includeInactive: options.includeInactive,
        }),
      ),
    select: (page) => page.data,
    enabled: can(scope.data, 'outlet:read') && Boolean(options.channelId),
    staleTime: 10 * 60_000,
  });
}

/**
 * EVERY classification, of every channel — for the report filter bar, now that
 * Channel takes more than one.
 *
 * ONE UNSCOPED REQUEST rather than one per channel. The master is a short list,
 * so ticking a second channel needs no request at all, and the caller needs the
 * whole of it anyway: unticking a channel has to find the ticked classifications
 * that belonged to it. The caller narrows the picker's options to the chosen
 * channels, and passes `enabled` false until one is chosen, for the reason
 * `useClassifications` gives.
 */
export function useAllClassifications(enabled: boolean) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['outlet-classifications', '', false],
    queryFn: () =>
      api.get<Page<OutletClassificationRow>>(
        withQuery('/masterdata/outlet-classifications', referencePage),
      ),
    select: (page) => page.data,
    enabled: can(scope.data, 'outlet:read') && enabled,
    staleTime: 10 * 60_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Paged variants — for the administration tables                             */
/* -------------------------------------------------------------------------- */

/**
 * These return the whole `Page<T>` rather than `.data`, because the table needs
 * `total` for its pager. They are separate hooks rather than a flag on the ones
 * above so that a picker cannot accidentally be given page 3 of its own options.
 *
 * `placeholderData` keeps the previous page on screen while the next one loads.
 * Without it every page change flashes the empty state, which reads as "your
 * filter matched nothing" for as long as the request takes.
 */

export function usePagedOutlets(
  params: PageParams<OutletSortKey> & {
    search?: string;
    channelId?: string;
    classificationId?: string;
    regionId?: string;
    includeInactive?: boolean;
  },
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['outlets', 'paged', params],
    queryFn: () => api.get<Page<OutletListRow>>(withQuery('/masterdata/outlets', params)),
    enabled: can(scope.data, 'outlet:read'),
    placeholderData: keepPreviousPage,
  });
}

export function usePagedChannels(
  params: PageParams<ChannelSortKey> & { includeInactive?: boolean },
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['channels', 'paged', params],
    queryFn: () => api.get<Page<ChannelRow>>(withQuery('/masterdata/channels', params)),
    enabled: can(scope.data, 'outlet:read'),
    placeholderData: keepPreviousPage,
  });
}

export function usePagedClassifications(
  params: PageParams<OutletClassificationSortKey> & {
    channelId?: string;
    includeInactive?: boolean;
  },
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['outlet-classifications', 'paged', params],
    queryFn: () =>
      api.get<Page<OutletClassificationRow>>(
        withQuery('/masterdata/outlet-classifications', params),
      ),
    enabled: can(scope.data, 'outlet:read'),
    placeholderData: keepPreviousPage,
  });
}

export function usePagedSkuCategories(
  params: PageParams<SkuCategorySortKey> & { includeInactive?: boolean },
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['sku-categories', 'paged', params],
    queryFn: () => api.get<Page<SkuCategoryRow>>(withQuery('/masterdata/sku-categories', params)),
    enabled: can(scope.data, 'sku:read'),
    placeholderData: keepPreviousPage,
  });
}

export function usePagedSkuGroupings(
  params: PageParams<SkuGroupingSortKey> & { includeInactive?: boolean },
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['sku-groupings', 'paged', params],
    queryFn: () => api.get<Page<SkuGroupingRow>>(withQuery('/masterdata/sku-groupings', params)),
    enabled: can(scope.data, 'sku:read'),
    placeholderData: keepPreviousPage,
  });
}

export function usePagedSkus(
  params: PageParams<SkuSortKey> & {
    search?: string;
    type?: string;
    categoryId?: string;
    groupingId?: string;
    division?: string;
    includeInactive?: boolean;
  },
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['skus', 'paged', params],
    queryFn: () => api.get<Page<SkuRow>>(withQuery('/masterdata/skus', params)),
    enabled: can(scope.data, 'sku:read'),
    placeholderData: keepPreviousPage,
  });
}

/**
 * FR-14 competition plans, for the admin table.
 *
 * Gated on `target:read`, not `sku:read` — a plan is a target, and the route it
 * calls is guarded by the same permission. Using the wrong one here would render
 * an empty table with no error rather than hiding the screen.
 */
export function usePagedCompetitionPlans(
  params: PageParams<CompetitionPlanSortKey> & {
    search?: string;
    internalSkuId?: string;
    /** 'KOMPETITOR' or 'INTERNAL' — a price ladder. Omitted means both. */
    pairKind?: SkuType;
    includeInactive?: boolean;
  },
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['competition-plans', 'paged', params],
    queryFn: () =>
      api.get<Page<CompetitionPlanRow>>(withQuery('/masterdata/competition-plans', params)),
    enabled: can(scope.data, 'target:read'),
    placeholderData: keepPreviousPage,
  });
}

export function usePagedRegions(params: PageParams<RegionSortKey> & { includeInactive?: boolean }) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['regions', 'paged', params],
    queryFn: () => api.get<Page<RegionRow>>(withQuery('/masterdata/regions', params)),
    enabled: can(scope.data, 'outlet:read'),
    placeholderData: keepPreviousPage,
  });
}

export function usePagedCompetitors(params: PageParams<CompetitorSortKey> & { includeInactive?: boolean }) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['competitors', 'paged', params],
    queryFn: () => api.get<Page<CompetitorRow>>(withQuery('/masterdata/competitors', params)),
    enabled: can(scope.data, 'sku:read'),
    placeholderData: keepPreviousPage,
  });
}

export function usePagedUsers(
  params: PageParams<UserSortKey> & {
    roleId?: string;
    permission?: Permission;
    search?: string;
    includeInactive?: boolean;
  },
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['users', 'paged', params],
    queryFn: () => api.get<Page<UserRow>>(withQuery('/auth/users', params)),
    enabled: can(scope.data, 'user:read'),
    placeholderData: keepPreviousPage,
  });
}

/**
 * Pending account-deletion requests — Google Play policy, UU 27/2022 Art. 8.
 *
 * THIS QUERY IS THE NOTIFICATION. There is no mail transport anywhere in the
 * API, deliberately, so nothing emails an administrator when somebody asks to be
 * deleted. The count this returns is what the users screen badges, and it is the
 * only thing standing between a request and the response time the published
 * deletion page commits to. Do not make it conditional on a screen being open.
 *
 * Its own query key, not a slice of `['users']`: an approval changes both, so
 * both are invalidated together, but a poll of one must not refetch the other.
 */
export function useDeletionRequests(
  params: PageParams<DeletionRequestSortKey> & { status?: AccountDeletionStatus },
) {
  const scope = useAccessScope();
  return useQuery({
    queryKey: ['deletion-requests', params],
    queryFn: () => api.get<Page<DeletionRequestRow>>(withQuery('/auth/deletion-requests', params)),
    enabled: can(scope.data, 'user:read'),
    placeholderData: keepPreviousPage,
  });
}
